import { useTranslation } from '../i18n';
import React, { useState, useEffect } from 'react';
import { Cascader, Spin } from 'antd';
import geoService from '../services/geoService';
import CountryFlag, { CountryLabel } from './CountryFlag';

const GeoSelector = ({ value = {}, onChange }) => {
    const { t } = useTranslation();
    const [options, setOptions] = useState([]);
    const [defaultStr, setDefaultStr] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetched, setFetched] = useState(false);
    const [selectedValues, setSelectedValues] = useState([]);

    useEffect(() => {
        fetchContinents();
    }, []);

    // 当 value 变化时，设置选中值并获取显示名称
    useEffect(() => {
        if (value) {
            // 将外部传入的值转换为组件内部的选中值格式
            const values = [];
            if (value.continent && value.continent !== '0') {
                values.push(parseInt(value.continent));

                if (value.country && value.country !== '0') {
                    values.push(parseInt(value.country));

                    if (value.province && value.province !== '0') {
                        values.push(parseInt(value.province));

                        if (value.city && value.city !== '0') {
                            values.push(parseInt(value.city));
                        }
                    }
                }
            }

            setSelectedValues(values);

            // 优先使用 value 中已有的名称字段，避免额外 API 调用
            if (values.length > 0) {
                const names = [];
                if (value.continent_name) {
                    names.push(value.continent_name);
                    if (value.country_name) {
                        names.push(value.country_name);
                        if (value.province_name) {
                            names.push(value.province_name);
                            if (value.city_name) {
                                names.push(value.city_name);
                            }
                        }
                    }
                }

                // 如果有名称字段，直接使用；否则调用 API 获取
                if (names.length > 0) {
                    setDefaultStr(names);
                } else {
                    fetchGeoNames(values);
                }
            }
        }
    }, [value]);

    // 获取地理位置的显示名称
    const fetchGeoNames = async (values) => {
        setLoading(true);
        try {
            const names = [];

            // 1. 获取大洲名称
            if (values[0]) {
                const continentResult = await geoService.getContinentInfo([values[0]]);
                if (continentResult.success && continentResult.data[values[0]]) {
                    // console.log("continentResult.data[values[0]]", continentResult.data[values[0]])
                    names.push(continentResult.data[values[0]].name);
                } else {
                    names.push(t('Continent {{value}}', { value: values[0] }));
                }

                // 2. 获取国家名称
                if (values[1]) {
                    const countryResult = await geoService.getCountryInfo([values[1]]);
                    if (countryResult.success && countryResult.data[values[1]]) {
                        // console.log("countryResult.data[values[1]]", countryResult.data[values[1]])
                        // 优先使用 country_name，如果没有则使用 country_code
                        const countryData = countryResult.data[values[1]];
                        names.push(countryData.country_name || countryData.name || countryData.country_code);
                    } else {
                        names.push(t('Country {{value}}', { value: values[1] }));
                    }

                    // 3. 获取省份名称
                    if (values[2]) {
                        const provinceResult = await geoService.getProvinceInfo([values[2]]);
                        if (provinceResult.success && provinceResult.data[values[2]]) {
                            // console.log("provinceResult.data[values[2]]", provinceResult.data[values[2]])
                            names.push(provinceResult.data[values[2]].name);
                        } else {
                            names.push(t('Province {{value}}', { value: values[2] }));
                        }

                        // 4. 获取城市名称
                        if (values[3]) {
                            const cityResult = await geoService.getCityInfo([values[3]]);
                            if (cityResult.success && cityResult.data[values[3]]) {
                                // console.log("cityResult.data[values[3]]", cityResult.data[values[3]])
                                names.push(cityResult.data[values[3]].name);
                            } else {
                                names.push(t('City {{value}}', { value: values[3] }));
                            }
                        }
                    }
                }
            }
            // 设置默认显示字符串
            setDefaultStr(names);
        } catch (error) {
            console.error('Error fetching geo names:', error);
            // 出错时使用 ID 作为备选
            setDefaultStr(values.map((v, i) => {
                const types = [t('Continent'), t('Country'), t('Province'), t('City')];
                return `${types[i]} ${v}`;
            }));
        } finally {
            setLoading(false);

        }
    };

    const fetchContinents = async () => {
        setLoading(true);
        try {
            const result = await geoService.getContinents();
            if (result.success) {
                const continentOptions = [
                    {
                        value: '0',
                        label: t('All continents'),
                        children: []
                    },
                    ...result.data.map(continent => ({
                        value: continent.id,
                        label: continent.continent,
                        isLeaf: false
                    }))
                ];
                setOptions(continentOptions);
            }
        } catch (error) {
            console.error('Error fetching continents:', error);
        } finally {
            if(value){
                setLoading(false);
            }
        }
    };

    const loadData = async (selectedOptions) => {
        const targetOption = selectedOptions[selectedOptions.length - 1];
        targetOption.loading = true;
        setFetched(true);

        try {
            // 根据选择的层级加载不同数据
            if (selectedOptions.length === 1) {
                // 加载国家
                const result = await geoService.getCountriesByContinent(targetOption.value);
                if (result.success) {
                    targetOption.children = [
                        {
                            value: '0',
                            label: t('All countries'),
                            children: []
                        },
                        ...result.data.map(country => ({
                            value: country.id,
                            label: country.country_name,
                            country_code: country.country_code,
                            isLeaf: false
                        }))
                    ];
                }
            } else if (selectedOptions.length === 2) {
                // 加载省份
                const result = await geoService.getRegions(targetOption.value);
                if (result.success) {
                    targetOption.children = [
                        {
                            value: '0',
                            label: t('All provinces'),
                            children: []
                        },
                        ...result.data.map(region => ({
                            value: region.id,
                            label: region.region_name,
                            isLeaf: false
                        }))
                    ];
                }
            } else if (selectedOptions.length === 3) {
                // 加载城市
                const countryId = selectedOptions[1].value;
                const result = await geoService.getCities(countryId, targetOption.value);
                if (result.success) {
                    targetOption.children = [
                        {
                            value: '0',
                            label: t('All cities'),
                            children: []
                        },
                        ...result.data.map(city => ({
                            value: city.id,
                            label: city.city_name,
                            isLeaf: true
                        }))
                    ];
                }
            }
        } catch (error) {
            console.error('Error loading cascader data:', error);
        } finally {
            targetOption.loading = false;
            setOptions([...options]);
        }
    };

    const handleChange = (values, selectedOptions) => {
        setSelectedValues(values);
        setFetched(true);

        // 查找 country_code（如果选择了国家）
        let countryCode = '';
        if (selectedOptions && selectedOptions.length >= 2 && selectedOptions[1]) {
            countryCode = selectedOptions[1].country_code || '';
        }

        // 构建传递给表单的值，包含ID和名称
        const result = {
            continent: (values && values[0]) || '0',
            country: (values && values[1]) || '0',
            province: (values && values[2]) || '0',
            city: (values && values[3]) || '0',
            country_code: countryCode,
            // 添加名称字段
            continent_name: selectedOptions && selectedOptions[0] ? selectedOptions[0].label : '',
            country_name: selectedOptions && selectedOptions[1] ? selectedOptions[1].label : '',
            province_name: selectedOptions && selectedOptions[2] ? selectedOptions[2].label : '',
            city_name: selectedOptions && selectedOptions[3] ? selectedOptions[3].label : ''
        };

        // 调用外部onChange
        if (onChange) {
            onChange(result);
        }
    };

    const displayRender = (label, selected) => {
        if (fetched) {
            const cc = selected && selected[1] && selected[1].country_code;
            return cc ? <><CountryFlag code={cc} />{label.join(' / ')}</> : label.join(' / ');
        }
        return defaultStr.length > 0 ? defaultStr.join(' / ') : selectedValues.join(' / ');
    };

    return (
        <Spin spinning={loading}>
            <Cascader
                options={options}
                loadData={loadData}
                onChange={handleChange}
                changeOnSelect
                displayRender={displayRender}
                optionRender={(o) => (o.country_code ? <CountryLabel code={o.country_code}>{o.label}</CountryLabel> : o.label)}
                value={selectedValues}
                placeholder={t('Select a location')}
            />
        </Spin>
    );
};

export default GeoSelector;
